import { ChangeDetectionStrategy, Component, computed, EventEmitter, input, Output } from '@angular/core';
import { ArrowLeft01Icon, ArrowRight01Icon } from '@hugeicons/core-free-icons/index';
import { LucideChevronLeft, LucideChevronRight } from '@lucide/angular';

export interface PaginationEvent {
  currentPage: number;
  pageSize: number;
  startIndex: number;
  endIndex: number;
}

export interface PageSizeOption {
  value: number;
  label: string;
}

@Component({
  selector: 'ptl-pagination-footer',
  templateUrl: './pagination-footer.component.html',
  styleUrls: ['./pagination-footer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaginationFooterComponent {
  totalItems = input<number>(0);
  pageSize = input<number>(10);
  currentPage = input<number>(1);
  flat = input<boolean>(false);
  pageSizeOptions = input<PageSizeOption[]>([
    { value: 10, label: '10' },
    { value: 25, label: '25' },
    { value: 50, label: '50' },
    { value: 100, label: '100' },
  ]);

  @Output() pageChange = new EventEmitter<PaginationEvent>();
  @Output() pageSizeChange = new EventEmitter<number>();

  protected readonly ArrowLeft01Icon = ArrowLeft01Icon;
  protected readonly ArrowRight01Icon = ArrowRight01Icon;

  effectivePageSizeOptions = computed<PageSizeOption[]>(() => {
    const options = this.pageSizeOptions();
    const size = this.pageSize();
    if (options.some((o) => o.value === size)) {
      return options;
    }
    return [...options, { value: size, label: `${size}` }].sort((a, b) => a.value - b.value);
  });

  totalPages = computed(() => Math.ceil(this.totalItems() / this.pageSize()));

  startItem = computed(() => {
    if (this.totalItems() === 0) return 0;
    return (this.currentPage() - 1) * this.pageSize() + 1;
  });

  endItem = computed(() => {
    const end = this.currentPage() * this.pageSize();
    return end > this.totalItems() ? this.totalItems() : end;
  });

  onPageSizeChange(newPageSize: number): void {
    this.pageSizeChange.emit(newPageSize);
  }

  onPreviousPage(): void {
    if (this.currentPage() > 1) {
      const newPage = this.currentPage() - 1;
      this.emitPageChange(newPage, this.pageSize());
    }
  }

  onNextPage(): void {
    if (this.currentPage() < this.totalPages()) {
      const newPage = this.currentPage() + 1;
      this.emitPageChange(newPage, this.pageSize());
    }
  }

  private emitPageChange(currentPage: number, pageSize: number): void {
    const event: PaginationEvent = {
      currentPage,
      pageSize,
      startIndex: (currentPage - 1) * pageSize,
      endIndex: Math.min(currentPage * pageSize - 1, this.totalItems() - 1),
    };
    this.pageChange.emit(event);
  }

  protected readonly LucideChevronLeft = LucideChevronLeft;
  protected readonly LucideChevronRight = LucideChevronRight;
}
